/**
 * 2 list pages для Income-tax-документів (Підпорція 3.2a, 2026-05-25).
 *
 * AG Grid list-only pages з auto-fill UX у editor через 3 buttons:
 *   - "З рах. 79" (fetch-from-account-79) для accounting_profit
 *   - "З нормувань" (sum-normalizations) для normalizations_addback
 *   - "Обчислити норму" (calculate-norm) для limit_base/rate
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  normalizations,
  incomeTaxCalculations,
  type Normalization,
  type IncomeTaxCalculation,
  type ExpenseType,
} from '@/api/incomeTax';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


const EXPENSE_TYPE_UA: Record<ExpenseType, string> = {
  REPRESENTATION: 'Представницькі',
  CHARITABLE: 'Благодійні',
  NON_TARGETED_CHARITY: 'Нецільова благодійність',
};


// ============================== Normalization ========================


export function IncomeTaxNormalizationsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['income-tax-norm'],
    queryFn: () => normalizations.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<Normalization>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'period_year', header: 'Рік', width: 90, sortValue: (r) => r.period_year, cell: (r) => r.period_year },
      { key: 'period_quarter', header: 'Квартал', width: 100, sortValue: (r) => r.period_quarter, cell: (r) => r.period_quarter },
      {
        key: 'expense_type', header: 'Тип витрат', width: 200,
        sortValue: (r) => EXPENSE_TYPE_UA[r.expense_type] ?? String(r.expense_type),
        cell: (r) => EXPENSE_TYPE_UA[r.expense_type] ?? String(r.expense_type),
      },
      {
        key: 'actual_amount', header: 'Факт', width: 140, align: 'right',
        sortValue: (r) => Number(r.actual_amount),
        cell: (r) => fmt2(r.actual_amount),
      },
      {
        key: 'limit_amount', header: 'Межа', width: 140, align: 'right',
        sortValue: (r) => Number(r.limit_amount),
        cell: (r) => fmt2(r.limit_amount),
      },
      {
        key: 'non_deductible_amount', header: 'Понад межу', width: 150, align: 'right',
        sortValue: (r) => Number(r.non_deductible_amount),
        cell: (r) => fmt2(r.non_deductible_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нормування витрат (податок на прибуток)</h2>
      </div>
      <DataTable<Normalization>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/taxation/income-tax/normalizations/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Calculation =========================


export function IncomeTaxCalculationsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['income-tax-calc'],
    queryFn: () => incomeTaxCalculations.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<IncomeTaxCalculation>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'period_year', header: 'Рік', width: 90, sortValue: (r) => r.period_year, cell: (r) => r.period_year },
      { key: 'period_quarter', header: 'Квартал', width: 100, sortValue: (r) => r.period_quarter, cell: (r) => r.period_quarter },
      {
        key: 'accounting_profit', header: 'Бух. прибуток', width: 160, align: 'right',
        sortValue: (r) => Number(r.accounting_profit),
        cell: (r) => fmt2(r.accounting_profit),
      },
      {
        key: 'taxable_profit', header: 'Опод. прибуток', width: 160, align: 'right',
        sortValue: (r) => Number(r.taxable_profit),
        cell: (r) => fmt2(r.taxable_profit),
      },
      {
        key: 'tax_amount', header: 'Податок', width: 150, align: 'right',
        sortValue: (r) => Number(r.tax_amount),
        cell: (r) => fmt2(r.tax_amount),
      },
      {
        key: 'tax_to_pay', header: 'До сплати', width: 150, align: 'right',
        sortValue: (r) => Number(r.tax_to_pay),
        cell: (r) => fmt2(r.tax_to_pay),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Розрахунки податку на прибуток</h2>
      </div>
      <DataTable<IncomeTaxCalculation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/taxation/income-tax/calculations/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
